import { Button } from 'components';
import { useMemo } from 'react';
import { DisplayTextKeys, OffersType, QUERY_KEYS, SetSlideoutConfigType, SlideoutConfigType } from 'views/Trade/types';
import { useQueryClient } from '@tanstack/react-query';
import useMutate from 'views/Trade/hooks/useMutate';
import { IDeleteOffersParams, deleteOffer } from 'views/Trade/services/deleteOffer';
import { QUERY_REFECH_TIMEOUT } from 'views/Trade/constants';
import { ViewStateType } from 'components/ProductTemplates/types';
import { FeedbackTemplate } from 'views/shared/FeedbackTemplate';
import { useTranslation } from 'react-i18next';
import { buildDisplayText } from 'utils';
import WineInfoTemplate from './WineInfoTemplate';

interface IAcceptBidOfMyOfferViewtProps {
  slideOutConfig: SlideoutConfigType;
  setSlideOutConfig: SetSlideoutConfigType;
}

const CancelOfferView = ({ slideOutConfig, setSlideOutConfig }: IAcceptBidOfMyOfferViewtProps) => {
  const { t } = useTranslation();
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'trade:common', t), [t]);

  const queryClient = useQueryClient();
  const { mutateAsync: mutateDeleteOffer } = useMutate({ mutationFn: deleteOffer });

  const cancelOffer = async (offer?: OffersType) => {
    const deleteOfferParams: IDeleteOffersParams = {
      headers: {
        emailAddress: 'email' || 'portal',
        operationType: 'DeleteSellOrder',
      },
      orderId: offer?.id as string,
    };

    try {
      setSlideOutConfig((prev) => ({
        ...prev,
        view: ViewStateType.LOADING,
      }));
      await mutateDeleteOffer(deleteOfferParams);
      setSlideOutConfig((prev) => ({
        ...prev,
        view: ViewStateType.THANK_YOU,
        templateConfig: { translationKey: 'common:offerCanceled' },
        isOpen: true,
      }));
    } catch (error) {
      setSlideOutConfig((prev) => ({
        ...prev,
        view: ViewStateType.ERROR_RESULT,
        onCTA: () =>
          setSlideOutConfig(() => ({
            ...prev,
            view: ViewStateType.DEFAULT,
          })),
      }));
    }

    setTimeout(() => {
      // only works if they are separately called
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_OFFERS] });
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS] });
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_STOCK] });
    }, QUERY_REFECH_TIMEOUT);
  };

  return (
    <FeedbackTemplate
      viewState={slideOutConfig?.view || ViewStateType.DEFAULT}
      templateConfig={slideOutConfig.templateConfig}
      onCTA={slideOutConfig.onCTA}
      onClose={() => {
        setSlideOutConfig({ isOpen: false });
      }}
    >
      <WineInfoTemplate config={slideOutConfig.wineInfoTemplateConfig}>
        <div className="flex flex-col flex-1 bg-white dark:bg-slate-900">
          {/* Message Card */}
          <div className="px-4 py-4">
            <div className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-800/10 rounded-xl p-6 border border-red-100 dark:border-red-700/30 shadow-sm">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-3">
                  <svg
                    className="w-6 h-6 text-red-500 dark:text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{`${
                  displayText[DisplayTextKeys.CANCEL_OFFER_MESSAGE]
                }`}</span>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="px-4 pb-4 mt-auto">
            <Button
              onClick={() => cancelOffer(slideOutConfig.offer)}
              type="button"
              className="w-full h-12 text-base font-semibold rounded-full btn bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 transition-all"
            >
              {displayText[DisplayTextKeys.CANCEL]}
            </Button>
          </div>
        </div>
      </WineInfoTemplate>
    </FeedbackTemplate>
  );
};

export default CancelOfferView;
